import { MutableRefObject, useEffect } from "react";
// React Query
import { useQueryClient } from "@tanstack/react-query";
// MUI
import { Divider } from "@mui/material";

// Components
import PendingRequest from "./Widgets/PendingRequest";
import TransferRequestHistory from "./Widgets/TransferRequestHistory";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Context
import { useInventoryContext } from "./contexts/InventoryFilterContext";

// API
import { onToggleDealerTradeBlockByLocation } from "@DataPages/api/inventory/subscriptions";

// Actions
import actions from "@Constants/actionsList";

// Types
import { TActionToDo } from "./types/dealerTrades";

type InventoryBodyProps = Readonly<{
	locationId: string;
	actionRef: MutableRefObject<TActionToDo>;
	isCurrentPayPeriod: boolean;
}>;

export default function DealerTrades(props: InventoryBodyProps) {
	const { locationId, actionRef, isCurrentPayPeriod } = props;

	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();
	const { useAuth } = useAuthorization();

	const hasPermissionToEdit = useAuth(actions.EDIT_DEALER_TRADES);

	const { commissionCycleId, payPeriodId } = useInventoryContext();

	// Update the cache when the subscription of the toggleDealerTradeBlock is triggered
	const dealerTradeBlockSubscription = getSubscription(["toggleDealerTradeBlockByLocation", locationId]);
	dealerTradeBlockSubscription?.subscriptions?.onToggleDealerTradeBlockByLocation?.setCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ["inventory", "pendingRequest", locationId],
			type: "all",
			refetchType: "all",
			exact: false,
		});
	});

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["toggleDealerTradeBlockByLocation", locationId],
				[
					{
						context: "onToggleDealerTradeBlockByLocation",
						query: onToggleDealerTradeBlockByLocation,
						variables: {
							locationId,
						},
					},
				]
			);
		}
	}, [locationId]);

	return (
		<>
			<Divider className="!mx-0 !mt-[32px] !border-[1px]" variant="middle" />
			<PendingRequest
				locationId={locationId}
				commissionCycleId={commissionCycleId}
				payPeriodId={payPeriodId}
				hasEditPermission={hasPermissionToEdit}
				actionRef={actionRef}
				isCurrentPayPeriod={isCurrentPayPeriod}
			/>
			<Divider className="!mx-0 !mt-[32px] !border-[1px]" variant="middle" />
			<TransferRequestHistory locationId={locationId} commissionCycleId={commissionCycleId} payPeriodId={payPeriodId} />
		</>
	);
}
